import { EventType } from "@/analytics/analytics-events";
import { IpxRegionOverLimitProperties } from "@/analytics/ipx-order-events-properties";
import {
  convertCubicMetersToCubicFeet,
  convertSquareMetersToSquareFeet,
  isMetric,
} from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import {
  selectIpxEstimatedRegionBytes,
  selectIpxFloorEstimate,
  selectIpxRegionFloorArea,
  selectIpxRegionVolume,
} from "@/store/modes/ipx-order-mode-selectors";
import { setIpxHighlightedFloor } from "@/store/modes/ipx-order-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { FaroText, grey } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { DistanceUnitTypes, humanReadableBytes } from "@faro-lotv/foundation";
import { Box, LinearProgress, Stack } from "@mui/material";
import { useEffect, useRef, type JSX } from "react";
import { floorColor } from "../floors/floor-colors";
import { IPX_MOCK_SIZE_LIMIT_BYTES } from "../ipx-config";

const METERS_PER_FOOT = 0.3048;

/** The last floors are dimmed while they are detected again */
const UPDATING_OPACITY = 0.6;

/** Shown while the value is not known yet, so the panel keeps its layout */
const PENDING_VALUE = "–";

/** Estimated number of points in the region, not computed yet */
export const ESTIMATED_POINT_COUNT_LABEL = PENDING_VALUE;

/** @returns the estimated size of the selected region compared to the IPX limit */
export function RegionSize(): JSX.Element {
  const bytes = useAppSelector(selectIpxEstimatedRegionBytes);
  const isOverLimit = bytes !== undefined && bytes > IPX_MOCK_SIZE_LIMIT_BYTES;

  const wasOverLimit = useRef(false);
  useEffect(() => {
    if (isOverLimit && !wasOverLimit.current) {
      Analytics.track<IpxRegionOverLimitProperties>(
        EventType.ipxRegionOverLimit,
        { sizeBytes: bytes },
      );
    }
    wasOverLimit.current = isOverLimit;
  }, [bytes, isOverLimit]);

  return (
    <Stack sx={{ gap: 0.5 }}>
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <FaroText variant="labelM">Estimated size</FaroText>
        <FaroText
          variant="labelM"
          color={isOverLimit ? "error.main" : grey[800]}
        >
          {bytes === undefined
            ? PENDING_VALUE
            : `~${humanReadableBytes(bytes, 1)} of ${humanReadableBytes(IPX_MOCK_SIZE_LIMIT_BYTES)}`}
        </FaroText>
      </Stack>
      <LinearProgress
        variant={bytes === undefined ? "indeterminate" : "determinate"}
        color={isOverLimit ? "error" : "primary"}
        value={
          bytes === undefined
            ? undefined
            : Math.min(100, (bytes / IPX_MOCK_SIZE_LIMIT_BYTES) * 100)
        }
        aria-label="estimated size compared to the IPX limit"
      />
    </Stack>
  );
}

/**
 * @param volume A volume in cubic meters
 * @param distanceUnit The unit of measure of the user
 * @returns the volume formatted in the unit system of the user
 */
export function formatVolume(
  volume: number,
  distanceUnit: DistanceUnitTypes,
): string {
  return isMetric(distanceUnit)
    ? `${Math.round(volume).toLocaleString()} m\u00B3`
    : `${Math.round(convertCubicMetersToCubicFeet(volume)).toLocaleString()} ft\u00B3`;
}

/**
 * @param area An area in square meters
 * @returns the area in square meters and feet, so it reads the same for FARO and IPX
 */
export function formatFloorArea(area: number): string {
  const squareFeet = convertSquareMetersToSquareFeet(area);
  return `~${Math.round(area).toLocaleString()} m\u00B2 (${Math.round(squareFeet).toLocaleString()} ft\u00B2)`;
}

/**
 * @param elevation A height in meters
 * @param distanceUnit The unit of measure of the user
 * @returns the height formatted in the unit system of the user, with its sign
 */
function formatElevation(
  elevation: number,
  distanceUnit: DistanceUnitTypes,
): string {
  const [value, unit] = isMetric(distanceUnit)
    ? [elevation, "m"]
    : [elevation / METERS_PER_FOOT, "ft"];
  return `${value >= 0 ? "+" : ""}${value.toFixed(1)} ${unit}`;
}

/** @returns the floors detected in the region, the rows highlight their floor in the scene */
function FloorList(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const distanceUnit = useDistanceUnit();
  const estimate = useAppSelector(selectIpxFloorEstimate);
  const highlighted = useAppSelector(
    (state) => state.ipxOrderMode.highlightedFloorIndex,
  );

  if (!estimate) return null;
  if (!estimate.floors.length) {
    return (
      <FaroText variant="helpText" color={grey[600]}>
        {estimate.status === "computing"
          ? "Detecting floors…"
          : "Floors not detected, the area is the box footprint"}
      </FaroText>
    );
  }

  const isUpdating = estimate.status === "computing";
  // Elevations relative to the lowest floor, the world heights mean nothing to the user
  const base = estimate.floors[0].elevation;
  return (
    <Stack
      component="ul"
      sx={{
        gap: 0.25,
        m: 0,
        p: 0,
        listStyle: "none",
        opacity: isUpdating ? UPDATING_OPACITY : 1,
      }}
      aria-label="detected floors"
      aria-busy={isUpdating}
      onMouseLeave={() => dispatch(setIpxHighlightedFloor(undefined))}
    >
      {isUpdating && (
        <FaroText component="li" variant="helpText" color={grey[600]}>
          Updating floors…
        </FaroText>
      )}
      {estimate.floors.map((floor, index) => (
        <Stack
          component="li"
          key={index}
          direction="row"
          onMouseEnter={() => dispatch(setIpxHighlightedFloor(index))}
          sx={{
            alignItems: "center",
            gap: 1,
            px: 0.5,
            borderRadius: 0.5,
            bgcolor: highlighted === index ? grey[100] : undefined,
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "2px",
              bgcolor: floorColor(index),
              flexShrink: 0,
            }}
          />
          <FaroText variant="bodyS" color={grey[600]} sx={{ flex: 1 }}>
            {`Floor ${index + 1} · ${formatElevation(floor.elevation - base, distanceUnit)}`}
          </FaroText>
          <FaroText variant="bodyS" color={grey[800]}>
            {formatFloorArea(floor.area)}
          </FaroText>
        </Stack>
      ))}
    </Stack>
  );
}

/** @returns the volume, gross floor area and estimated point count of the region box */
function RegionMeasures(): JSX.Element {
  const distanceUnit = useDistanceUnit();
  const volume = useAppSelector(selectIpxRegionVolume);
  const floorArea = useAppSelector(selectIpxRegionFloorArea);

  return (
    <Stack sx={{ gap: 0.5 }}>
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <FaroText variant="labelM">Volume</FaroText>
        <FaroText variant="labelM" color={grey[800]}>
          {volume === undefined
            ? PENDING_VALUE
            : formatVolume(volume, distanceUnit)}
        </FaroText>
      </Stack>
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <FaroText variant="labelM">Gross floor area (GFA)</FaroText>
        <FaroText variant="labelM" color={grey[800]}>
          {floorArea === undefined ? PENDING_VALUE : formatFloorArea(floorArea)}
        </FaroText>
      </Stack>
      <Stack direction="row" sx={{ justifyContent: "space-between" }}>
        <FaroText variant="labelM">Estimated points</FaroText>
        <FaroText variant="labelM" color={grey[800]}>
          {ESTIMATED_POINT_COUNT_LABEL}
        </FaroText>
      </Stack>
      <FloorList />
    </Stack>
  );
}

/** @returns the volume, floor area and estimated size of the region */
export function RegionPanel(): JSX.Element {
  return (
    <Stack sx={{ gap: 2 }}>
      <RegionMeasures />
      <RegionSize />
    </Stack>
  );
}
